import { useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Back, meaning the page this one was opened from.
 *
 * `navigate(-1)` alone is wrong for a screen that can also be reached by its own
 * URL — an emailed link to a user, a bookmarked report — where it walks out of the
 * app and into whatever the browser had before. A hardcoded route is wrong the
 * other way: it lands on page 1 of a list the reader was on page 7 of.
 *
 * So: the history entry when this app put one there, and the fallback route when
 * it did not. `location.key` is `'default'` only for the entry the app was loaded
 * on, which is exactly the case where there is nothing of ours to go back to.
 *
 * @param {string} fallback  where this screen belongs when it was opened directly
 */
export default function useGoBack(fallback) {
  const navigate = useNavigate()
  const { key } = useLocation()

  /* THE KEY THIS SCREEN ARRIVED ON, not the one it is on now.
   *
   * A screen that keeps its state in the address — a page number, a tab, a search — rewrites
   * its own URL with `replace`, and react-router mints a fresh key each time. So a report
   * opened from a bookmark started as `default`, wrote `?page=2` to the address, and from
   * then on looked exactly like a screen that had been opened from somewhere: Back walked
   * out of the app and into whatever the browser had before it.
   *
   * Whether there is anything of ours to go back to is decided by how this screen was
   * ENTERED, and that does not change while you are on it. */
  const entryKey = useRef(key)

  return useCallback(() => {
    if (entryKey.current && entryKey.current !== 'default') navigate(-1)
    else navigate(fallback)
  }, [navigate, fallback])
}
